import { memo } from "react"
import { useTranslation } from "react-i18next"
import {
  RiCloseLine,
  RiFileTextLine,
  RiGridLine,
  RiBarChartHorizontalLine,
  RiLineChartLine,
  RiTableLine,
  RiMapPinLine,
  RiPieChartLine,
  RiBubbleChartLine,
} from "@remixicon/react"

import type { CanvasItem as CanvasItemType } from "@/types/pdfBuilder"
import { usePdfBuilderStore } from "@/stores/pdfBuilder"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import LoadingSpinner from "@/app/components/LoadingSpinner"
import ChartRenderer from "../../charts/ChartRenderer"
import CanvasItemErrorBoundary from "./CanvasItemErrorBoundary"

interface CanvasItemProps {
  item: CanvasItemType
  isSelected: boolean
  /** 고른 컴포넌트와 같은 줄인가 — 줄 맞추기용 옅은 테두리 (2026-10-02 요청) */
  isRowPeer?: boolean
  /** 키보드 조작을 스토어에 적용(재배치)하는 동안 — 칸 위에 "적용 중" 을 덮는다 */
  isBusy?: boolean
  onDelete: () => void
}

/**
 * 번역이 아직 없는 종류의 영문 이름 — 팔레트(ChartPalette)의 이름과 같게 둔다.
 * 빠뜨리면 머리 라벨에 chartType 이 그대로 보인다 (2026-10-02 보고: spacer)
 */
const FALLBACK_LABELS: Record<string, string> = {
  activityInfo: "Activity Info",
  summary: "Summary",
  combo: "Chart by Player",
  activityGraph: "Chart by Activity",
  bar: "Bar Chart",
  line: "Line Chart",
  point: "Point Chart",
  scatter: "Scatter Chart",
  table: "Table",
  sessionDetails: "Session Details",
  comparison: "Comparison",
  spacer: "Spacer",
}

function getChartIcon(chartType: string) {
  switch (chartType) {
    case "activityInfo":
      return RiFileTextLine
    case "summary":
      return RiGridLine
    case "bar":
      return RiBarChartHorizontalLine
    case "line":
    case "combo":
    case "activityGraph":
    case "point":
    case "sessionDetails":
      return RiLineChartLine
    case "scatter":
      return RiBubbleChartLine
    case "table":
      return RiTableLine
    case "map":
      return RiMapPinLine
    case "comparison":
      return RiPieChartLine
    default:
      return RiFileTextLine
  }
}

function CanvasItem({
  item,
  isSelected,
  isRowPeer = false,
  isBusy = false,
  onDelete,
}: CanvasItemProps) {
  const { t } = useTranslation()
  const filterActivityId = usePdfBuilderStore((s) => s.filterActivityId)
  // 트렌드 리포트는 액티비티를 고르지 않는다 — 기간이 곧 데이터 범위다 (2026-10-02 요청)
  const isTrend = usePdfBuilderStore((s) => s.filterReportKind === "trend")
  // 기간의 액티비티 목록·대상 액티비티가 정해지기 전에는 모든 컴포넌트를 로딩으로 덮는다 —
  // 처음 들어왔을 때 "액티비티를 고르세요"가 잠깐 비쳤다 데이터로 바뀌는 깜빡임을 없앤다 (2026-10-02 요청)
  const { isFilterResolving } = usePdfDataContext()
  // 페이지 경계에 걸린 아이템은 컴포넌트가 계산한 위 여백만큼 내용(제목 포함)을 다음 페이지로 내린다 (2026-09-30 요청)
  const headGap = usePdfBuilderStore((s) => s.headGaps[item.id] ?? 0)
  // 표 묶음(Table, 세션 단위로 나눈 Action Map, 보기마다 나눈 Session Details 컨테이너)의 둘째 장부터는 삭제 버튼이 없다 —
  // 첫 장에서만 통째로 지운다 (2026-09-28 요청). 옛 Session Details 4개 묶음(intervalViews 없음)은 각각 지운다
  const isFollowUpTablePage = usePdfBuilderStore((s) => {
    const groupId = item.options.groupId
    const isSplitContainer =
      item.chartType === "table" ||
      item.chartType === "map" ||
      (item.chartType === "sessionDetails" && Array.isArray(item.options.intervalViews))
    if (!isSplitContainer || !groupId) {
      return false
    }
    const page = s.pages.find((p) => p.id === s.currentPageId)

    return (page?.items ?? []).some(
      (other) =>
        other.id !== item.id &&
        other.options.groupId === groupId &&
        (other.y < item.y || (other.y === item.y && other.x < item.x)),
    )
  })

  const IconComponent = getChartIcon(item.chartType)
  const customTitle = item.options.title
  // map 은 배포된 번역(chartSectionMap = "Map")을 피해 새 키로 "Action Map" 을 쓴다 (2026-09-29 개편)
  const sectionLabel =
    item.chartType === "map"
      ? t("chartSectionActionMap", "Action Map")
      : t(
          `chartSection${item.chartType.charAt(0).toUpperCase() + item.chartType.slice(1)}`,
          FALLBACK_LABELS[item.chartType] ?? item.chartType,
        )

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete()
  }

  // 스페이서는 데이터도 제목도 없는 빈 칸이다
  const hasData =
    isTrend ||
    !!filterActivityId ||
    item.chartType === "activityInfo" ||
    item.chartType === "spacer"

  // 제목 영역을 아예 두지 않는 컴포넌트 (편집 화면·PDF 모두).
  // 요약은 카드 자체가 지표를 설명하므로 위에 제목을 또 붙이지 않는다.
  const hideTitle =
    item.chartType === "activityInfo" ||
    item.chartType === "spacer" ||
    item.chartType === "summary" ||
    isFollowUpTablePage

  const showHeader = !hideTitle && !!customTitle

  return (
    <div
      className={`CanvasItem ${isSelected ? "CanvasItem--selected" : ""} ${isRowPeer ? "CanvasItem--row-peer" : ""}`}
      style={headGap > 0 ? { paddingTop: 8 + headGap } : undefined}>
      {/* Floating chip - absolute positioned */}
      <div className="CanvasItem__chip">
        <span>{sectionLabel}</span>
        {!isFollowUpTablePage && (
          <button onClick={handleDeleteClick} title={t("button.delete", "Delete")}>
            <RiCloseLine size={14} />
          </button>
        )}
      </div>
      {isBusy && (
        <div className="CanvasItem__busy">
          <span className="CanvasItem__busy-spinner" />
        </div>
      )}
      <div className="CanvasItem__content">
        {showHeader && (
          <div className="CanvasItem__header">
            <h4 className="CanvasItem__title">{customTitle}</h4>
          </div>
        )}
        <div className="CanvasItem__chart">
          {isFilterResolving ? (
            <div className="CanvasItem__placeholder">
              <LoadingSpinner />
            </div>
          ) : hasData ? (
            // 아이템 하나가 그리다 터져도 이 칸만 안내로 바뀐다. 설정(dataSource·options)이 바뀌면 다시 그려 본다
            <CanvasItemErrorBoundary
              itemId={item.id}
              chartType={item.chartType}
              title={item.options.title}
              resetKey={JSON.stringify([item.dataSource, item.options, filterActivityId])}>
              <ChartRenderer item={item} />
            </CanvasItemErrorBoundary>
          ) : (
            <div className="CanvasItem__placeholder">
              <IconComponent size={32} />
              <p>{t("configureDataSource", "Configure data source")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(CanvasItem)
